// src/pages/auth/ForgetPasswordPage.jsx
import PropTypes from "prop-types";
import React from "react";
import {
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  Container,
  FormFeedback,
  Input,
  Label,
  Form
} from "reactstrap";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";
import { useForgotPassword } from "../../api/auth";
import * as Yup from "yup";
import { useFormik } from "formik";
import { createSearchParams } from "react-router-dom";
import CustomHeader from "../../components/Auth/CustomHeader";
import profile from "../../assets/images/profile-img.png";

const ForgetPasswordPage = (props) => {
  const { t } = props;

  // meta title
  document.title = t("forgotPasswordTitle") || "Forgot Password | JobArk";

  const { mutateAsync, isPending, isError, error } = useForgotPassword();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { email: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("forgotPasswordEmailInvalid"))
        .required(t("forgotPasswordEmailRequired"))
    }),
    onSubmit: async (values) => {
      try {
        const data = await mutateAsync({ email: values.email.trim() });
        const token = data?.token ?? data?.data?.token ?? "";
        const search = createSearchParams({
          email: values.email.trim(),
          token
        }).toString();
        props.router.navigate({
          pathname: "/verify-otp-forgot-pwd",
          search: `?${search}`
        });
      } catch (e) {
        // error rendered below
        console.error(e);
      }
    }
  });

  return (
    <React.Fragment>
      <CustomHeader />

      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-soft-primary">
                  <Row>
                    <Col xs={7}>
                      <div className="text-primary p-4">
                        <h5 className="text-primary">{t("forgotPasswordHeading")}</h5>
                        <p>{t("forgotPasswordSubheading")}</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img src={profile} alt="" className="img-fluid" />
                    </Col>
                  </Row>
                </div>

                <CardBody className="pt-0">
                  <div className="p-2">
                    {isError && (
                      <Alert color="danger" style={{ marginTop: 13 }}>
                        {error?.message || t("forgotPasswordGenericError")}
                      </Alert>
                    )}

                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="email">
                          {t("forgotPasswordEmailLabel")}
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          className="form-control"
                          placeholder={t("forgotPasswordEmailPlaceholder")}
                          type="email"
                          autoComplete="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={validation.touched.email && !!validation.errors.email}
                        />
                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                      </div>

                      <Row className="mb-3">
                        <Col className="text-end">
                          <button
                            className="btn btn-secondary w-md"
                            type="submit"
                            disabled={isPending}
                          >
                            {isPending ? t("forgotPasswordSubmitting") : t("forgotPasswordSubmit")}
                          </button>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p>
                  {t("forgotPasswordBackToLoginPrefix")}{" "}
                  <Link to="/login" className="font-weight-medium text-primary">
                    {t("forgotPasswordBackToLoginLink")}
                  </Link>
                </p>
                <p>© 2025 JobArk. {t("allRightsReserved", "All Rights Reserved")}</p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ForgetPasswordPage.propTypes = {
  router: PropTypes.shape({
    navigate: PropTypes.func,
    location: PropTypes.object,
    params: PropTypes.object
  })
};

export default withRouter(withTranslation()(ForgetPasswordPage));
